import React, { useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Card, Button, Row, Col, Breadcrumb, Tabs, Tab, Spinner, Form, InputGroup, ButtonGroup, Container } from 'react-bootstrap';
import { AppContext } from '../Context/index';
import { obtenerUsuarios } from '../firebase/firebase';
import { Link } from 'react-router-dom';
import { FaSearch, FaUserAlt, FaClipboardList, FaGraduationCap, FaList, FaEnvelope, FaUsers, FaTimes } from 'react-icons/fa';
import { LiaThLargeSolid } from 'react-icons/lia';
import NavbarGlobal from './NavbarGlobal';
import './ListadoAlumnos.scss';
import { useAlumnosOcultos } from '../hooks/useAlumnosOcultos';
import { BotonVisibilidadAlumno, FiltroAlumnosOcultos } from '../components/alumnos/VisibilidadAlumnos';

const defaultProfilePicture = '/img/default-profile.jpg';
const normalizeText = (value = '') => value
  .toString()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .trim()
  .toLowerCase();

const isGraduado = (alumno) => {
  const estatus = normalizeText(alumno?.estatus);
  return estatus.includes('graduado') || estatus.includes('certificado');
};

const ListadoAlumnos = () => {
  const [alumnosOriginales, setAlumnosOriginales] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const { currentUser } = useContext(AppContext);
  const visibilidad = useAlumnosOcultos(currentUser);
  const uidActual = currentUser?.uid || currentUser?.id;
  const esMaster = Boolean(currentUser?.isMaster);
  const esProfesor = Boolean(currentUser?.teacher);
  const alumnosVisibles = useMemo(() => alumnosOriginales.filter(visibilidad.esVisible), [alumnosOriginales, visibilidad.esVisible]);
  const cantidadOcultos = useMemo(() => alumnosOriginales.filter(visibilidad.estaOculto).length, [alumnosOriginales, visibilidad.estaOculto]);
  const [searchQuery, setSearchQuery] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [activeTab, setActiveTab] = useState('todos');
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('alumnosViewMode') || 'grid');
  const searchTimeoutRef = useRef(null);

  const fetchAlumnos = useCallback(async () => {
    if (!uidActual) {
      setError('No se pudo identificar al usuario actual');
      return;
    }

    try {
      setCargando(true);
      setError('');

      const todosLosUsuarios = await obtenerUsuarios();
      const soloAlumnos = todosLosUsuarios.filter(usuario => !usuario.soloClasificacion && !usuario.teacher && !usuario.isMaster && usuario.isSuperAdmin !== true);
      let alumnosFiltrados = [];

      if (esMaster) {
        alumnosFiltrados = soloAlumnos;
      } else if (esProfesor && !esMaster) {
        const teacherUid = uidActual;

        if (!teacherUid) {
          setError('No se pudo identificar el UID del profesor.');
          return;
        }

        alumnosFiltrados = soloAlumnos.filter(alumno => alumno.teacherId === teacherUid);
      } else {
        setError('No tienes permisos para ver alumnos');
        return;
      }

      setAlumnosOriginales(alumnosFiltrados);
    } catch (error) {
      console.error('Error al cargar alumnos: ', error);
      setError('No se pudieron cargar los alumnos.');
    } finally {
      setCargando(false);
    }
  }, [uidActual, esMaster, esProfesor]);

  useEffect(() => {
    if (uidActual) {
      fetchAlumnos();
    }
  }, [fetchAlumnos, uidActual]);

  const alumnos = useMemo(() => {
    if (!searchQuery.trim()) return alumnosVisibles;
    const termino = searchQuery.toLowerCase().trim();
    return alumnosVisibles.filter(alumno =>
      alumno.displayName?.toLowerCase().includes(termino) ||
      alumno.name?.toLowerCase().includes(termino) ||
      alumno.email?.toLowerCase().includes(termino) ||
      alumno.matricula?.toLowerCase().includes(termino)
    );
  }, [searchQuery, alumnosVisibles]);

  const handleSearchChange = useCallback((event) => {
    const valor = event.target.value;
    setInputValue(valor);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(valor);
    }, 300);
  }, []);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const clearSearch = useCallback(() => {
    setInputValue('');
    setSearchQuery('');

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  }, []);

  const toggleViewMode = useCallback((mode) => {
    setViewMode(mode);
    localStorage.setItem('alumnosViewMode', mode);
  }, []);

  const alumnosPorNivel = useMemo(() => ({
    todos: alumnos,
    nivel1: alumnos.filter(alumno => alumno.level === '1' && !isGraduado(alumno)),
    nivel2: alumnos.filter(alumno => alumno.level === '2' && !isGraduado(alumno)),
    nivel3: alumnos.filter(alumno => alumno.level === '3' && !isGraduado(alumno)),
    sinNivel: alumnos.filter(alumno => !alumno.level && !isGraduado(alumno)),
    graduados: alumnos.filter(isGraduado),
  }), [alumnos]);

  const resumenPorNivel = useMemo(() => {
    const reales = alumnosOriginales.filter(visibilidad.cuentaEnEstadisticas);
    return {
      total: reales.length,
      nivel1: reales.filter(alumno => alumno.level === '1').length,
      nivel2: reales.filter(alumno => alumno.level === '2').length,
      nivel3: reales.filter(alumno => alumno.level === '3').length,
      sinNivel: reales.filter(alumno => !alumno.level).length,
      graduados: reales.filter(isGraduado).length,
    };
  }, [alumnosOriginales, visibilidad.cuentaEnEstadisticas]);

  const tabsConfig = useMemo(() => ([
    { eventKey: 'todos', label: 'Todos', count: alumnosPorNivel.todos.length, icon: <FaUsers /> },
    { eventKey: 'nivel1', label: 'Nivel 1', count: alumnosPorNivel.nivel1.length },
    { eventKey: 'nivel2', label: 'Nivel 2', count: alumnosPorNivel.nivel2.length },
    { eventKey: 'nivel3', label: 'Nivel 3', count: alumnosPorNivel.nivel3.length },
    { eventKey: 'sinNivel', label: 'Sin nivel', count: alumnosPorNivel.sinNivel.length },
    { eventKey: 'graduados', label: 'Graduados / Certificados', count: alumnosPorNivel.graduados.length, icon: <FaGraduationCap /> },
  ]), [alumnosPorNivel]);

  const getNivelBadgeClass = useCallback((level) => {
    if (!level) return 'nivel-badge sin-nivel';
    return `nivel-badge nivel-${level}`;
  }, []);

  const getNivelLabel = useCallback((level) => {
    if (!level) return 'Sin nivel';
    return `Nivel ${level}`;
  }, []);

  const getAlumnoNombre = useCallback((alumno) => (
    alumno.displayName || alumno.name || 'Alumno sin nombre'
  ), []);

  const renderTarjetaAlumno = useCallback((alumno) => {
    const nombre = getAlumnoNombre(alumno);

    return (
      <Col xs={12} sm={6} lg={4} xl={3} key={alumno.id} className="students-grid__item">
        <Card className="user-card glass h-100">
          <Card.Body>
            <div className="student-card__top">
              <img
                src={alumno.profilePicture || defaultProfilePicture}
                alt={nombre}
                className="student-card__avatar"
              />
              <div className="student-card__badges">
                <span className={getNivelBadgeClass(alumno.level)}>
                  {getNivelLabel(alumno.level)}
                </span>
                {isGraduado(alumno) && (
                  <span className="graduado-badge"><FaGraduationCap /> Graduado / Certificado</span>
                )}
              </div>
            </div>

            <div className="student-card__body">
              <Card.Title>{nombre}</Card.Title>
              <Card.Text>
                <FaEnvelope />
                <span>{alumno.email || 'Sin correo registrado'}</span>
              </Card.Text>
            </div>

            <div className="student-card__actions">
              <BotonVisibilidadAlumno alumno={alumno} visibilidad={visibilidad} />
              <Button
                variant="primary"
                as={Link}
                to={`/dashboard/listado-alumnos/detalle-alumno/${alumno.id}`}
                className="btn-transition"
              >
                <FaUserAlt /> Ver ficha
              </Button>
              <Button
                variant="outline-primary"
                as={Link}
                to={`/dashboard/listado-alumnos/registro-de-practicas/${alumno.id}`}
                className="btn-transition"
              >
                <FaClipboardList /> Cuaderno digital
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Col>
    );
  }, [getAlumnoNombre, getNivelBadgeClass, getNivelLabel, visibilidad]);

  const renderFilaAlumno = useCallback((alumno) => {
    const nombre = getAlumnoNombre(alumno);

    return (
      <div className="list-view-item" key={alumno.id}>
        <div className="list-view-item__identity">
          <div className="avatar-container">
            <img
              src={alumno.profilePicture || defaultProfilePicture}
              alt={nombre}
              className="avatar-img"
            />
            <span className={`nivel-indicator nivel-${alumno.level || 'none'}`}></span>
          </div>

          <div className="list-view-item__details">
            <h3>{nombre}</h3>
            <div className="list-view-item__email">
              <FaEnvelope /> {alumno.email || 'Sin correo registrado'}
            </div>
          </div>
        </div>

        <div className="list-view-item__meta">
          <span className={getNivelBadgeClass(alumno.level)}>
            {getNivelLabel(alumno.level)}
          </span>
          {isGraduado(alumno) && (
            <span className="graduado-badge"><FaGraduationCap /> Graduado / Certificado</span>
          )}
        </div>

        <div className="list-actions">
          <BotonVisibilidadAlumno alumno={alumno} visibilidad={visibilidad} />
          <Button
            variant="outline-primary"
            size="sm"
            as={Link}
            to={`/dashboard/listado-alumnos/detalle-alumno/${alumno.id}`}
            className="btn-transition"
          >
            <FaUserAlt /> Ver ficha
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            as={Link}
            to={`/dashboard/listado-alumnos/registro-de-practicas/${alumno.id}`}
            className="btn-transition"
          >
            <FaClipboardList /> Cuaderno digital
          </Button>
        </div>
      </div>
    );
  }, [getAlumnoNombre, getNivelBadgeClass, getNivelLabel, visibilidad]);

  const renderContenidoTab = useCallback((alumnosNivel) => {
    if (alumnosNivel.length === 0) {
      return (
        <div className="empty-state empty-state--compact">
          <FaGraduationCap />
          <p>No hay alumnos en esta sección.</p>
        </div>
      );
    }

    return viewMode === 'grid' ? (
      <Row className="students-grid">
        {alumnosNivel.map(renderTarjetaAlumno)}
      </Row>
    ) : (
      <div className="list-view">
        {alumnosNivel.map(renderFilaAlumno)}
      </div>
    );
  }, [viewMode, renderTarjetaAlumno, renderFilaAlumno]);

  if (!currentUser) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Cargando información del usuario...</p>
      </div>
    );
  }

  if (cargando) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Cargando alumnos...</p>
      </div>
    );
  }

  if (error) return <div className="alert alert-danger">{error}</div>;

  const pageTitle = currentUser.isMaster ? 'Listado General de Alumnos' : 'Mis Alumnos Asignados';
  const pageDescription = currentUser.isMaster
    ? 'Vista general para revisar alumnos, fichas y avance del cuaderno digital.'
    : 'Consulta tus alumnos asignados y entra rápido a sus fichas o resultados.';

  return (
    <div className="listado-alumnos pb-4">
      <NavbarGlobal title={pageTitle} showBackButton={true} />

      <Container className="listado-alumnos__container">
        <Breadcrumb className="listado-alumnos__breadcrumb">
          <Breadcrumb.Item href="/dashboard">Inicio</Breadcrumb.Item>
          <Breadcrumb.Item active>{pageTitle}</Breadcrumb.Item>
        </Breadcrumb>

        <section className="students-hero glass">
          <div className="students-hero__copy">
            <span className="students-hero__eyebrow">
              {currentUser.isMaster ? 'Gestión general' : 'Panel profesor'}
            </span>
            <h1>{pageTitle}</h1>
            <p>{pageDescription}</p>
          </div>

          <div className="students-hero__stats" aria-label="Resumen de alumnos por nivel">
            <div className="summary-stat summary-stat--total">
              <span>{resumenPorNivel.total}</span>
              <small>Alumnos reales</small>
            </div>
            <div className="summary-stat">
              <span>{resumenPorNivel.nivel1}</span>
              <small>Nivel 1</small>
            </div>
            <div className="summary-stat">
              <span>{resumenPorNivel.nivel2}</span>
              <small>Nivel 2</small>
            </div>
            <div className="summary-stat">
              <span>{resumenPorNivel.nivel3}</span>
              <small>Nivel 3</small>
            </div>
            <div className="summary-stat">
              <span>{resumenPorNivel.sinNivel}</span>
              <small>Sin nivel</small>
            </div>
            <div className="summary-stat summary-stat--graduados">
              <span>{resumenPorNivel.graduados}</span>
              <small>Graduados</small>
            </div>
          </div>
        </section>

        <FiltroAlumnosOcultos visibilidad={visibilidad} cantidad={cantidadOcultos} />
        <section className="students-toolbar glass" aria-label="Herramientas del listado">
          <div className="search-container">
            <label htmlFor="student-search">Buscar alumno</label>
            <InputGroup>
              <InputGroup.Text>
                <FaSearch />
              </InputGroup.Text>
              <Form.Control
                id="student-search"
                type="text"
                placeholder="Nombre, correo o matrícula"
                value={inputValue}
                onChange={handleSearchChange}
                autoComplete="off"
              />
              {inputValue && (
                <Button
                  variant="outline-primary"
                  onClick={clearSearch}
                  aria-label="Limpiar búsqueda"
                >
                  <FaTimes />
                </Button>
              )}
            </InputGroup>
            {searchQuery && (
              <div className="search-helper">
                <FaSearch /> "{searchQuery}" · {alumnos.length} resultado{alumnos.length !== 1 ? 's' : ''}
              </div>
            )}
          </div>

          <div className="view-controls">
            <span>Vista</span>
            <ButtonGroup>
              <Button
                variant={viewMode === 'grid' ? 'primary' : 'outline-primary'}
                onClick={() => toggleViewMode('grid')}
                className="btn-transition"
                aria-label="Vista de cuadrícula"
              >
                <LiaThLargeSolid /> Cuadrícula
              </Button>
              <Button
                variant={viewMode === 'list' ? 'primary' : 'outline-primary'}
                onClick={() => toggleViewMode('list')}
                className="btn-transition"
                aria-label="Vista de lista"
              >
                <FaList /> Lista
              </Button>
            </ButtonGroup>
          </div>
        </section>

        {alumnosVisibles.length === 0 && !cargando && !error && (
          <div className="empty-state glass">
            <FaUsers />
            <h2>No hay alumnos para mostrar</h2>
            <p>{visibilidad.mostrarOcultos ? 'No tienes alumnos ocultos.' : cantidadOcultos ? 'Puedes activar «Mostrar alumnos ocultos» para revisar tus alumnos ocultos.' : currentUser.isMaster ? 'Aún no hay alumnos registrados.' : 'No tienes alumnos asignados.'}</p>
          </div>
        )}

        {alumnosVisibles.length > 0 && alumnos.length === 0 && searchQuery && (
          <div className="empty-state glass">
            <FaSearch />
            <h2>Sin resultados</h2>
            <p>No se encontraron alumnos con ese criterio de búsqueda.</p>
            <Button variant="primary" onClick={clearSearch}>Limpiar búsqueda</Button>
          </div>
        )}

        {alumnos.length > 0 && (
          <section className="students-results animate-fade-in">
            <Tabs
              activeKey={activeTab}
              onSelect={(key) => setActiveTab(key || 'todos')}
              className="custom-tabs"
              variant="pills"
            >
              {tabsConfig.map(tab => (
                <Tab
                  key={tab.eventKey}
                  eventKey={tab.eventKey}
                  title={
                    <span className="tab-label">
                      {tab.icon}
                      <span>{tab.label}</span>
                      <span className="tab-count">{tab.count}</span>
                    </span>
                  }
                >
                  <div className="students-results__panel glass">
                    {renderContenidoTab(alumnosPorNivel[tab.eventKey])}
                  </div>
                </Tab>
              ))}
            </Tabs>
          </section>
        )}
      </Container>
    </div>
  );
};

export { ListadoAlumnos };
